import CloseIcon from '@mui/icons-material/Close'
import LogoutIcon from '@mui/icons-material/Logout'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import AssessmentIcon from '@mui/icons-material/Assessment'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import CodeIcon from '@mui/icons-material/Code'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import Alert from '@mui/material/Alert'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import Fade from '@mui/material/Fade'
import IconButton from '@mui/material/IconButton'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { initializeApp } from 'firebase/app'
import {
  confirmPasswordReset,
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup
} from 'firebase/auth'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import {
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom'
import './App.css'
import './i18n'

import LogoAsr from './assets/logo_voxist_asr.svg'

import { Avatar, Menu, MenuItem, Typography, Button, Divider } from '@mui/material'
import Reset from 'routes/reset'
import SendReset from 'routes/sendReset'
import Form from './routes/form'
import Home from './routes/home'
import DeveloperDocs from './routes/docs'
import AdminPage from './routes/admin'
import BillingModal from './components/BillingModal'
import UsageModal from './components/UsageModal'
import ApiKeysModal from './components/ApiKeysModal'
import { useTranslation } from 'react-i18next'

const API_BASE = window.__ENV__?.API_BASE || 'http://localhost:3000'

const AuthContext = createContext(null)

const AlertContext = createContext(null)

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)
  const [adminLoading, setAdminLoading] = useState(false)

  // Fetch user profile to check admin status
  const fetchUserProfile = async (token: string) => {
    setAdminLoading(true)
    try {
      const response = await fetch(`${API_BASE}/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (response.ok) {
        const profile = await response.json()
        setIsAdmin(profile.role === 'admin')
      } else {
        setIsAdmin(false)
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
      setIsAdmin(false)
    } finally {
      setAdminLoading(false)
    }
  }

  // Load user from session storage on init
  useEffect(() => {
    const storedToken = sessionStorage.getItem('access_token')
    const storedUser = sessionStorage.getItem('user')

    if (storedToken && storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        setUser({
          ...userData,
          token: storedToken,
          getIdToken: async () => storedToken
        })
        // Fetch admin status for stored session
        fetchUserProfile(storedToken)
      } catch (error) {
        console.error('Failed to parse stored user:', error)
        sessionStorage.removeItem('access_token')
        sessionStorage.removeItem('refresh_token')
        sessionStorage.removeItem('user')
      }
    }
  }, [])
  const navigate = useNavigate()

  const { setAlert } = useAlert()
  const { t } = useTranslation()

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (userOb) => {
      if (userOb) {
        // console.log(userOb)
        setUser(userOb)
        setAlert({ type: 'success', text: t('success') })
        // Fetch admin status for Firebase users
        try {
          const token = await userOb.getIdToken()
          fetchUserProfile(token)
        } catch (error) {
          console.error('Failed to get token for admin check:', error)
        }
      } else {
        setUser(null)
        setIsAdmin(false)
      }
    })
    return () => {
      unsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getToken = async () => {
    try {
      if (user?.token) {
        // Internal database authentication
        return user.token
      } else if (auth.currentUser) {
        // Firebase authentication
        const token = await auth.currentUser.getIdToken()
        return token
      }
      throw new Error('No authenticated user')
    } catch (error) {
      setAlert({ type: 'error', text: t('error') })
      throw error
    }
  }
  const sendReset = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email)
      navigate('/')
    } catch (error) {
      setAlert({ type: 'error', text: t('error') })
    }
  }
  const resetPass = async (oob, password) => {
    try {
      await confirmPasswordReset(auth, oob, password)
      navigate('/')
    } catch (error) {
      setAlert({ type: 'error', text: t('error') })
    }
  }

  const signIn = async (email, password) => {
    try {
      // Use internal database authentication instead of Firebase
      const response = await fetch(`${window.__ENV__?.API_BASE || 'http://localhost:3000'}/oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          grant_type: 'password',
          username: email,
          password: password,
          scope: 'self_manage transcription_send billing_manage',
        }),
      })

      if (!response.ok) {
        throw new Error('Authentication failed')
      }

      const tokenData = await response.json()
      
      // Store tokens in session storage
      sessionStorage.setItem('access_token', tokenData.access_token)
      sessionStorage.setItem('refresh_token', tokenData.refresh_token)
      
      // Create a pseudo user object compatible with Firebase user structure
      const pseudoUser = {
        uid: `internal_${email}`,
        email: email,
        token: tokenData.access_token,
        refresh_token: tokenData.refresh_token,
        getIdToken: async () => tokenData.access_token
      }
      
      // Store user data in session storage (without tokens to avoid duplication)
      const userForStorage = {
        uid: pseudoUser.uid,
        email: pseudoUser.email
      }
      sessionStorage.setItem('user', JSON.stringify(userForStorage))
      
      setUser(pseudoUser)
      setAlert({ type: 'success', text: t('success') })
      return pseudoUser
    } catch (error) {
      setAlert({ type: 'error', text: t('error') })
      throw error
    }
  }

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider()
      return await signInWithPopup(auth, provider)
    } catch (error) {
      setAlert({ type: 'error', text: t('error') })
    }
  }

  const signInWithMicrosoft = async () => {
    try {
      const provider = new OAuthProvider('microsoft.com')
      return await signInWithPopup(auth, provider)
    } catch (error) {
      setAlert({ type: 'error', text: t('error') })
    }
  }

  const signOut = async () => {
    if (user?.token) {
      // Internal database authentication - clear local state and session storage
      sessionStorage.removeItem('access_token')
      sessionStorage.removeItem('refresh_token')
      sessionStorage.removeItem('user')
      setUser(null)
      setIsAdmin(false)
    } else {
      // Firebase authentication
      await auth.signOut()
      setIsAdmin(false)
    }
  }

  const value = { user, isAdmin, adminLoading, signIn, signInWithGoogle, signInWithMicrosoft, signOut, getToken, sendReset, resetPass }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const useAuth = () => useContext(AuthContext)

const useQuery = () => {
  const { search } = useLocation()

  return useMemo(() => new URLSearchParams(search), [search])
}
const RequireAuth = ({ children }) => {
  const auth = useAuth()
  const location = useLocation()

  if (!auth.user) {
    return <Navigate to="/" state={{ from: location }} replace />
  }

  return children
}

const CheckAuth = ({ children }) => {
  const auth = useAuth()
  const location = useLocation()

  if (auth.user) {
    return <Navigate to="/form" state={{ from: location }} replace />
  }

  return children
}

const RequireAdmin = ({ children }) => {
  const auth = useAuth()
  const location = useLocation()

  if (!auth.user) {
    return <Navigate to="/" state={{ from: location }} replace />
  }

  if (!auth.isAdmin) {
    return <Navigate to="/form" state={{ from: location }} replace />
  }

  return children
}

const AlertProvider = ({ children }) => {
  const [alert, setAlert] = useState(null)
  const value = { alert, setAlert }

  return <AlertContext.Provider value={value}>{children}</AlertContext.Provider>
}

const useAlert = () => useContext(AlertContext)

const Layout = () => {
  const auth = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [anchorEl, setAnchorEl] = useState(null)
  const [billingModalOpen, setBillingModalOpen] = useState(false)
  const [usageModalOpen, setUsageModalOpen] = useState(false)
  const [apiKeysModalOpen, setApiKeysModalOpen] = useState(false)
  const { t } = useTranslation()

  const theme = useTheme()
  const { alert, setAlert } = useAlert()

  useEffect(() => {
    let timeout = null
    if (alert) {
      timeout = setTimeout(() => setAlert(null), 5000)
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alert])

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  return (
    <AppBar position="static" elevation={0}>
      <Toolbar
        style={{
          backgroundColor: 'transparent',
          top: 0,
          zIndex: 1000,
          right: 0,
          left: 0,
          padding: 16,
          alignItems: 'center',
          justifyContent:
            location?.pathname !== '/home' ? 'space-between' : 'center',
          alignContent: 'center'
        }}
      >
{location?.pathname !== '/' && (
          <Box display="flex" alignItems="center" gap={2}>
            <Tooltip title="Home">
              <Link
                to="/"
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignContent: 'center',
                  textDecoration: 'none'
                }}
              >
                <img src={LogoAsr} style={{ width: 54, height: 54 }} alt="icon" />
                <Typography
                  variant="h6"
                  style={{
                    color: 'white',
                    marginLeft: 10,
                    textDecoration: 'none'
                  }}
                >
                  {t('appTitle')}
                </Typography>
              </Link>
            </Tooltip>
            
{auth?.user && (
              <Button
                onClick={() => navigate('/docs')}
                startIcon={<CodeIcon />}
                sx={{
                  color: '#4caf50',
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  border: '1px solid rgba(76, 175, 80, 0.3)',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(76, 175, 80, 0.2)',
                    border: '1px solid rgba(76, 175, 80, 0.5)',
                  },
                }}
              >
                {t('developerDocs')}
              </Button>
            )}
          </Box>
        )}
        {/* {!auth?.user && (
            <Tooltip title="reset">
              <Link
                to="/resetLink"
                style={{
                  flexGrow: 1,
                  textAlign: 'center',
                  textDecoration: 'none',
                  color: theme.palette.text.primary
                }}
              >
                <Typography
                  variant="body1"
                  component="div"
                  sx={{ flexGrow: 1, width: '100%', textAlign: 'center' }}
                >
                  reset
                </Typography>
              </Link>
            </Tooltip>
          )} */}

        <Box display="flex" alignItems="center" gap={1}>
          {auth?.user && (
            <Box>
              <Tooltip title={t('userMenu')}>
                <IconButton onClick={handleMenuOpen}>
                  <Avatar
                    sx={{ bgcolor: '#00CFF3', color: theme.palette.text.primary }}
                  />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem disabled sx={{ opacity: 1 }}>
                  <Typography variant="body2" color="text.primary" fontWeight="500">
                    {auth?.user?.email}
                  </Typography>
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setBillingModalOpen(true)
                    handleMenuClose()
                  }}
                >
                  <AccountBalanceWalletIcon sx={{ mr: 2 }} />
                  {t('billing')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setUsageModalOpen(true)
                    handleMenuClose()
                  }}
                >
                  <AssessmentIcon sx={{ mr: 2 }} />
                  {t('usage')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setApiKeysModalOpen(true)
                    handleMenuClose()
                  }}
                >
                  <VpnKeyIcon sx={{ mr: 2 }} />
                  {t('apiKeys')}
                </MenuItem>
                {auth?.isAdmin && (
                  <>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        navigate('/admin')
                        handleMenuClose()
                      }}
                    >
                      <AdminPanelSettingsIcon sx={{ mr: 2 }} />
                      {t('admin')}
                    </MenuItem>
                  </>
                )}
                <MenuItem
                  onClick={() => {
                    auth.signOut(() => navigate('/'))
                    handleMenuClose()
                  }}
                >
                  <LogoutIcon sx={{ mr: 2 }} />
                  {t('signOut')}
                </MenuItem>
              </Menu>
            </Box>
          )}
        </Box>
      </Toolbar>
      <Outlet />
      <Box sx={{ bottom: 0, position: 'fixed', right: 0, zIndex: 1001 }}>
        <Fade in={Boolean(alert)} unmountOnExit>
          <Alert
            severity={alert?.type}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setAlert(null)
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            sx={{ mb: 2 }}
          >
            {alert?.text}
          </Alert>
        </Fade>
      </Box>

      {/* Developer Portal Modals */}
      <BillingModal open={billingModalOpen} onClose={() => setBillingModalOpen(false)} />
      <UsageModal open={usageModalOpen} onClose={() => setUsageModalOpen(false)} />
      <ApiKeysModal open={apiKeysModalOpen} onClose={() => setApiKeysModalOpen(false)} />
    </AppBar>
  )
}

const firebaseConfig = {
  apiKey: 'AIzaSyCQdH1EsUt1VIYm5CyT8K2DhHbntFc7aw0',
  authDomain: 'asr-voxist.firebaseapp.com',
  databaseURL:
    'https://asr-voxist-default-rtdb.europe-west1.firebasedatabase.app',
  projectId: 'asr-voxist',
  storageBucket: 'asr-voxist.appspot.com',
  messagingSenderId: '705011226493',
  appId: '1:705011226493:web:cfd71e448889ee055cde14',
  measurementId: 'G-K14CB3Q7DV'
}

const app = initializeApp(firebaseConfig)

const auth = getAuth(app)

export { useAlert, useAuth, useQuery }

const App = () => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
  const theme = useMemo(
    () =>
      createTheme({
        components: {
          MuiInputBase: {
            styleOverrides: {
              input: {
                '&:-webkit-autofill': {
                  transitionDelay: '9999s',
                  transitionProperty: 'background-color, color'
                }
              }
            }
          },
          MuiFilledInput: {
            styleOverrides: {
              input: {
                '&:-webkit-autofill': {
                  WebkitBoxShadow: 'inherit',
                  WebkitTextFillColor: 'inherit',
                  caretColor: 'inherit',
                  borderRadius: 'inherit'
                }
              }
            }
          },
          MuiOutlinedInput: {
            styleOverrides: {
              input: {
                '&:-webkit-autofill': {
                  WebkitBoxShadow: 'inherit',
                  WebkitTextFillColor: 'inherit',
                  caretColor: 'inherit',
                  borderRadius: 'inherit'
                }
              }
            }
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                background: '#2E3B55'
              },
              colorDefault: {
                background: 'transparent'
              }
            }
          },
          MuiToolbar: {
            styleOverrides: {
              root: {
                background: 'transparent'
              }
            }
          }
        },
        palette: {
          mode: prefersDarkMode ? 'dark' : 'light',
          ...(prefersDarkMode
            ? {
                // palette values for light mode
                background: {
                  default: '#000000',
                  paper: '#121212'
                }
              }
            : {
                // palette values for dark mode

                background: {
                  default: '#ffffff',
                  paper: '#f8f8f8'
                }
              })
        }
      }),
    [prefersDarkMode]
  )
  return (
    <AlertProvider>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Routes>
            <Route element={<Layout />}>
              <Route
                path="/"
                element={
                  <CheckAuth>
                    <Home />
                  </CheckAuth>
                }
              />
              <Route
                path="/resetLink"
                element={
                  <CheckAuth>
                    <SendReset />
                  </CheckAuth>
                }
              />
              <Route
                path="/reset"
                element={
                  <CheckAuth>
                    <Reset />
                  </CheckAuth>
                }
              />

              <Route
                path="/form"
                element={
                  <RequireAuth>
                    <Form />
                  </RequireAuth>
                }
              ></Route>
              
              <Route
                path="/docs"
                element={
                  <RequireAuth>
                    <DeveloperDocs />
                  </RequireAuth>
                }
              ></Route>

              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <AdminPage />
                  </RequireAdmin>
                }
              ></Route>
            </Route>
          </Routes>
        </ThemeProvider>
      </AuthProvider>
    </AlertProvider>
  )
}

export default App
